<template>
  <header class="site-header">
    <router-link class="site-logo" to="/">
      <img v-if="logoUrl" :src="logoUrl" :alt="siteName">
      <span v-else>{{ siteName }}</span>
    </router-link>
    <nav class="desktop-nav" aria-label="Primary">
      <div
        v-for="item in site.nav"
        :key="item.label"
        class="desktop-nav__item"
        :class="{ 'desktop-nav__item--active': isActiveItem(item) }"
      >
        <component
          :is="isExternal(item.href) ? 'a' : 'router-link'"
          class="desktop-nav__link"
          :class="{ 'desktop-nav__link--active': isActiveItem(item) }"
          v-bind="linkAttrs(item.href)"
        >
          {{ displayNavLabel(item) }}
        </component>
        <div v-if="item.children" class="desktop-nav__dropdown">
          <component
            :is="isExternal(child.href) ? 'a' : 'router-link'"
            v-for="child in item.children"
            :key="child.label"
            class="desktop-nav__dropdown-link"
            :class="{ 'desktop-nav__dropdown-link--active': isActiveHref(child.href) }"
            v-bind="linkAttrs(child.href)"
          >
            {{ displayNavLabel(child) }}
          </component>
        </div>
      </div>
    </nav>
    <div class="site-header__actions">
      <label class="language-select" :aria-label="$t('common.selectLanguage')">
        <select :value="$lang.current" @change="handleLanguageChange">
          <option v-for="language in $supportedLanguages" :key="language.code" :value="language.code">
            {{ language.label }}
          </option>
        </select>
      </label>
      <button class="icon-button" type="button" :aria-label="$t('search.open')" @click="toggleSearch(true)">
        <span class="icon-button__search"></span>
      </button>
      <button
        class="menu-button"
        type="button"
        :aria-expanded="String(menuOpen)"
        :aria-label="$t('common.openMenu')"
        @click="toggleMenu"
      >
        <span></span>
        <span></span>
        <span></span>
      </button>
    </div>

    <transition name="fade">
      <div v-if="searchOpen" class="overlay overlay--search">
        <div class="overlay__bar">
          <div class="site-logo site-logo--dark">
            <img v-if="logoUrl" :src="logoUrl" :alt="siteName">
            <span v-else>{{ siteName }}</span>
          </div>
          <button class="close-button" type="button" :aria-label="$t('search.close')" @click="toggleSearch(false)">
            <span></span>
            <span></span>
          </button>
        </div>
        <div class="search-panel">
          <p class="search-panel__eyebrow">{{ $t('search.eyebrow') }}</p>
          <div class="search-panel__field">
            <input
              v-model.trim="searchQuery"
              type="text"
              :placeholder="$t('search.placeholder')"
            >
          </div>
          <div class="search-panel__results">
            <button
              v-for="(item, index) in filteredSearchItems"
              :key="item.label + '-' + item.group + '-' + index"
              class="search-result"
              type="button"
              @click="handleSearchJump(item.href)"
            >
              <span>{{ item.label }}</span>
              <small>{{ item.group }}</small>
            </button>
          </div>
        </div>
      </div>
    </transition>

    <transition name="fade">
      <div v-if="menuOpen" class="overlay overlay--menu">
        <div class="overlay__bar">
          <div class="site-logo site-logo--dark">
            <img v-if="logoUrl" :src="logoUrl" :alt="siteName">
            <span v-else>{{ siteName }}</span>
          </div>
          <button class="close-button" type="button" aria-label="Close menu" @click="toggleMenu">
            <span></span>
            <span></span>
          </button>
        </div>
        <nav class="menu-panel">
          <div class="menu-panel__primary">
            <button
              v-for="item in site.nav"
              :key="item.label"
              class="menu-panel__item"
              type="button"
              :class="{ 'menu-panel__item--active': activeMenuItem === item.label }"
              @click="handleMenuItem(item)"
            >
              <span>{{ displayNavLabel(item) }}</span>
              <span
                v-if="item.children"
                class="menu-panel__arrow"
                :class="{ 'menu-panel__arrow--open': activeMenuItem === item.label }"
              ></span>
            </button>
          </div>
          <div class="menu-panel__secondary">
            <p>{{ activeMenuItem ? displayNavLabel({ label: activeMenuItem }) : 'STRONGOVA' }}</p>
            <div v-if="activeMenuChildren.length" class="menu-panel__submenu">
              <button
                v-for="child in activeMenuChildren"
                :key="child.label"
                class="menu-panel__subitem"
                type="button"
                @click="navigate(child.href)"
              >
                {{ displayNavLabel(child) }}
              </button>
            </div>
            <div v-else class="menu-panel__note">
              Natural wood flooring, made for premium residential, hospitality, and commercial projects.
            </div>
          </div>
        </nav>
      </div>
    </transition>
  </header>
</template>

<script>
import { resolveAssetUrl } from '../api/public'

export default {
  name: 'SiteHeader',
  props: {
    site: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      menuOpen: false,
      searchOpen: false,
      searchQuery: '',
      activeMenuItem: 'Products'
    }
  },
  computed: {
    siteName () {
      return (this.site.siteSettings && this.site.siteSettings.siteName) || 'STRONGOVA'
    },
    logoUrl () {
      const logoImage = this.site.siteSettings && this.site.siteSettings.logoImage
      return logoImage ? resolveAssetUrl(logoImage) : ''
    },
    activeMenuChildren () {
      const current = this.site.nav.find(item => item.label === this.activeMenuItem)
      return current && current.children ? current.children : []
    },
    filteredSearchItems () {
      const pool = [
        ...this.site.nav.map(item => ({ label: this.displayNavLabel(item), group: this.$t('search.navigation'), href: item.href })),
        ...this.site.interests.items.map(item => ({ label: item.title, group: this.$t('search.interest'), href: item.href })),
        ...this.site.nav
          .filter(item => item.children)
          .reduce((items, item) => items.concat(item.children.map(child => ({
            label: this.displayNavLabel(child),
            group: this.displayNavLabel(item),
            href: child.href
          }))), []),
        ...this.site.productsPage.categories.map(item => ({
          label: item.title,
          group: this.$t('search.product'),
          href: '/products-list/' + item.slug
        })),
        ...this.site.coloursPage.families.map(item => ({
          label: item.title,
          group: this.$t('nav.colours'),
          href: '/products-list/' + item.slug
        })),
        ...this.productSearchItems.map(item => ({
          label: [item.code, item.title].filter(Boolean).join(' '),
          group: item.category || this.$t('search.product'),
          href: '/products-page/' + item.slug
        })),
        ...this.caseSearchItems.map(item => ({
          label: [item.title, item.country].filter(Boolean).join(' '),
          group: this.$t('search.case'),
          href: '/cases'
        })),
        ...this.articleSearchItems.map(item => ({
          label: item.title,
          group: this.$t('search.article'),
          href: '/news/' + item.slug
        }))
      ]

      if (!this.searchQuery) {
        return pool.slice(0, 8)
      }

      const keyword = this.searchQuery.toLowerCase()
      return pool.filter(item => item.label.toLowerCase().includes(keyword)).slice(0, 8)
    },
    productSearchItems () {
      const backendItems = this.site.searchData && this.site.searchData.products
      if (backendItems && backendItems.length) return backendItems
      return Object.keys(this.site.productItems).map(slug => ({
        ...this.site.productItems[slug],
        slug
      }))
    },
    caseSearchItems () {
      const backendItems = this.site.searchData && this.site.searchData.cases
      return backendItems && backendItems.length ? backendItems : this.site.casesPage.items
    },
    articleSearchItems () {
      const backendItems = this.site.searchData && this.site.searchData.articles
      return backendItems && backendItems.length ? backendItems : this.site.knowledgePage.articles
    }
  },
  methods: {
    currentSection () {
      const path = this.$route.path
      if (path === '/') return 'home'
      if (path.startsWith('/products') || path.startsWith('/colours')) return 'products'
      if (path.startsWith('/customize')) return 'customize'
      if (path.startsWith('/strength') || path.startsWith('/quality') || path.startsWith('/cases')) return 'strength'
      if (path.startsWith('/knowledge') || path.startsWith('/care')) return 'knowledge'
      if (path.startsWith('/contact')) return 'contact'
      return ''
    },
    isExternal (href) {
      return /^https?:\/\//.test(href)
    },
    normalizedHref (href) {
      return href.replace(/\/+$/, '') || '/'
    },
    isActiveItem (item) {
      return item.target === this.currentSection()
    },
    isActiveHref (href) {
      if (this.isExternal(href)) return false
      const currentFullPath = this.normalizedHref(this.$route.fullPath)
      const normalized = this.normalizedHref(href)
      if (normalized.includes('?')) {
        return currentFullPath === normalized
      }
      return this.$route.path === normalized
    },
    linkAttrs (href) {
      return this.isExternal(href) ? { href } : { to: href }
    },
    displayNavLabel (item) {
      const keyMap = {
        Home: 'nav.home',
        Products: 'nav.products',
        Customize: 'nav.customize',
        Strength: 'nav.strength',
        Knowledge: 'nav.knowledge',
        Contact: 'nav.contact',
        'Natural Wood Floors': 'nav.naturalWoodFloors',
        Plank: 'nav.plank',
        'Herringbone Plank 90°': 'nav.herringbonePlank90',
        'Chevron Plank 45°': 'nav.chevronPlank45',
        Parquet: 'nav.parquet',
        'Oversized Plank': 'nav.oversizedPlank',
        Colours: 'nav.colours',
        'Light Wood Floors': 'nav.lightWoodFloors',
        'Dark Wood Floors': 'nav.darkWoodFloors',
        Advantage: 'nav.advantage',
        'Premium Quality': 'nav.premiumQuality',
        Cases: 'nav.cases',
        News: 'nav.news',
        Care: 'nav.care',
        'Quote Quickly': 'nav.quoteQuickly',
        'Sales Partner': 'nav.salesPartner',
        Factory: 'nav.factory'
      }
      return keyMap[item.label] ? this.$t(keyMap[item.label]) : item.label
    },
    handleLanguageChange (event) {
      const next = this.$setLang(event.target.value)
      this.menuOpen = false
      this.searchOpen = false
      this.syncOverlayState()
      window.setTimeout(() => {
        window.location.reload()
      }, next ? 20 : 0)
    },
    toggleMenu () {
      this.menuOpen = !this.menuOpen
      if (this.menuOpen) {
        this.searchOpen = false
      }
      this.syncOverlayState()
    },
    toggleSearch (state) {
      this.searchOpen = state
      if (state) {
        this.menuOpen = false
      }
      this.syncOverlayState()
    },
    handleMenuItem (item) {
      if (item.children) {
        this.activeMenuItem = this.activeMenuItem === item.label ? '' : item.label
        return
      }

      this.navigate(item.href)
    },
    handleSearchJump (href) {
      this.searchOpen = false
      this.navigate(href)
    },
    navigate (href) {
      this.menuOpen = false
      this.searchOpen = false
      this.syncOverlayState()
      if (this.isExternal(href)) {
        window.location.href = href
        return
      }
      this.$router.push(href)
    },
    syncOverlayState () {
      this.$nextTick(() => {
        document.body.classList.toggle('is-overlay-open', this.menuOpen || this.searchOpen)
      })
    }
  },
  beforeDestroy () {
    document.body.classList.remove('is-overlay-open')
  }
}
</script>

<style>
.site-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 40;
  display: grid;
  grid-template-columns: minmax(160px, 1fr) auto minmax(160px, 1fr);
  align-items: center;
  gap: 32px;
  height: 72px;
  padding: 0 42px;
  background: rgba(12, 12, 12, 0.84);
  backdrop-filter: blur(14px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.site-logo {
  display: inline-flex;
  align-items: center;
  justify-self: start;
  font-size: 25px;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.site-logo img {
  display: block;
  width: auto;
  height: 32px;
  max-width: 178px;
  object-fit: contain;
}

.site-logo--dark {
  color: var(--dark-text);
}

.site-header__actions {
  display: flex;
  align-items: stretch;
  grid-column: 3;
  justify-self: end;
  margin-left: 0;
}

.language-select {
  display: flex;
  align-items: center;
  height: 72px;
  padding: 0 12px;
}

.language-select select {
  height: 36px;
  max-width: 128px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 999px;
  padding: 0 28px 0 12px;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(245, 243, 239, 0.9);
  font: inherit;
  font-size: 14px;
  outline: none;
  cursor: pointer;
}

.language-select option {
  color: #171411;
}

.desktop-nav {
  display: flex;
  align-items: stretch;
  justify-self: center;
  height: 100%;
  margin-left: 0;
}

.desktop-nav__item {
  position: relative;
  display: flex;
  align-items: center;
}

.desktop-nav__link {
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0 24px;
  font-size: 17px;
  letter-spacing: 0.01em;
  color: rgba(245, 243, 239, 0.8);
  transition: color 0.2s ease;
}

.desktop-nav__link:hover,
.desktop-nav__item:hover .desktop-nav__link {
  color: #fff;
}

.desktop-nav__link--active {
  color: #fff;
}

.desktop-nav__item--active::after {
  content: "";
  position: absolute;
  left: 24px;
  right: 24px;
  bottom: 0;
  height: 2px;
  background: var(--accent);
}

.desktop-nav__dropdown {
  position: absolute;
  top: calc(100% + 1px);
  left: 0;
  min-width: 250px;
  padding: 10px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  background: rgba(16, 16, 16, 0.96);
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.3);
  opacity: 0;
  visibility: hidden;
  transform: translateY(10px);
  transition: opacity 0.22s ease, transform 0.22s ease, visibility 0.22s ease;
}

.desktop-nav__item:hover .desktop-nav__dropdown {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.desktop-nav__dropdown-link {
  display: block;
  padding: 12px 15px;
  border-radius: 8px;
  font-size: 16px;
  color: rgba(245, 243, 239, 0.74);
  white-space: nowrap;
}

.desktop-nav__dropdown-link:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.05);
}

.desktop-nav__dropdown-link--active {
  color: #fff;
  background: rgba(255, 255, 255, 0.08);
}

.icon-button,
.menu-button,
.close-button {
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
}

.icon-button {
  width: 64px;
  height: 72px;
  display: grid;
  place-items: center;
  color: rgba(245, 243, 239, 0.88);
}

.icon-button__search {
  position: relative;
  width: 20px;
  height: 20px;
  border: 2px solid currentColor;
  border-radius: 50%;
}

.icon-button__search::after {
  content: "";
  position: absolute;
  right: -5px;
  bottom: -5px;
  width: 8px;
  height: 2px;
  background: currentColor;
  transform: rotate(45deg);
}

.menu-button,
.close-button {
  width: 72px;
  height: 72px;
  display: grid;
  place-items: center;
  background: #f3eee6;
  color: var(--dark-text);
}

.menu-button span {
  display: block;
  width: 24px;
  height: 1px;
  background: currentColor;
  margin: 3px 0;
}

.close-button {
  position: relative;
}

.close-button span {
  position: absolute;
  width: 24px;
  height: 1px;
  background: currentColor;
}

.close-button span:first-child {
  transform: rotate(45deg);
}

.close-button span:last-child {
  transform: rotate(-45deg);
}

.overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
  display: block;
  width: 100vw;
  min-height: 100vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.overlay--menu,
.overlay--search {
  background-color: #f5f0e8;
  color: var(--dark-text);
}

.overlay__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 60px;
  padding-left: 34px;
  border-bottom: 1px solid rgba(23, 20, 17, 0.08);
}

.menu-panel,
.search-panel {
  width: min(1360px, calc(100vw - 64px));
  margin: 0 auto;
}

.menu-panel {
  display: grid;
  grid-template-columns: minmax(300px, 0.8fr) minmax(420px, 1.4fr);
  gap: 72px;
  padding-top: 58px;
  padding-bottom: 72px;
  background: #f5f0e8;
}

.menu-panel__item,
.menu-panel__subitem,
.search-result {
  width: 100%;
  border: 0;
  background: transparent;
  cursor: pointer;
  color: #333;
}

.menu-panel__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 0;
  font-size: 34px;
  font-weight: 300;
  color: var(--dark-text);
  border-bottom: 1px solid rgba(23, 20, 17, 0.12);
}

.menu-panel__item--active {
  color: #7a6041;
}

.menu-panel__arrow {
  width: 12px;
  height: 12px;
  border-top: 1px solid currentColor;
  border-right: 1px solid currentColor;
  transform: rotate(45deg);
  transition: transform 0.25s ease;
}

.menu-panel__arrow--open {
  transform: rotate(135deg);
}

.menu-panel__submenu {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.menu-panel__subitem {
  min-height: 70px;
  padding: 18px;
  border: 1px solid rgba(23, 20, 17, 0.12);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.34);
  text-align: left;
  font-size: 16px;
  transition: background 0.2s ease, color 0.2s ease;
}

.menu-panel__subitem:hover {
  background: var(--dark-text);
  color: var(--text);
}

.menu-panel__secondary p {
  margin: 0 0 24px;
  font-size: 15px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(23, 20, 17, 0.58);
}

.menu-panel__note {
  max-width: 520px;
  font-size: 28px;
  line-height: 1.4;
  color: rgba(23, 20, 17, 0.74);
}

.search-panel {
  padding-top: 72px;
}

.search-panel__eyebrow {
  margin: 0 0 18px;
  font-size: 15px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(23, 20, 17, 0.58);
}

.search-panel__field input {
  width: 100%;
  padding: 18px 0;
  border: 0;
  border-bottom: 1px solid rgba(23, 20, 17, 0.14);
  border-radius: 0;
  background: transparent;
  font-size: clamp(34px, 5vw, 54px);
  color: var(--dark-text);
  outline: none;
}

.search-panel__results {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
  padding-top: 28px;
}

.search-result {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 0;
  border-bottom: 1px solid rgba(23, 20, 17, 0.1);
  color: var(--dark-text);
}

.search-result small {
  color: rgba(23, 20, 17, 0.54);
}

@media (min-width: 1100px) {
  .menu-button {
    display: none;
  }
}

@media (max-width: 1099px) {
  .desktop-nav {
    display: none;
  }
}

@media (max-width: 900px) {
  .site-header {
    grid-template-columns: minmax(0, 1fr) auto;
    height: 66px;
    padding: 0 0 0 18px;
    gap: 12px;
  }

  .site-header__actions {
    grid-column: 2;
  }

  .site-logo img {
    max-width: 148px;
    height: 27px;
  }

  .icon-button {
    width: 56px;
    height: 66px;
  }

  .language-select {
    height: 66px;
  }

  .language-select {
    padding: 0 6px;
  }

  .language-select select {
    max-width: 96px;
    padding-left: 10px;
  }

  .menu-button,
  .close-button {
    width: 62px;
    height: 66px;
  }

  .overlay__bar {
    padding-left: 18px;
  }

  .menu-panel,
  .search-panel {
    width: calc(100vw - 32px);
  }

  .menu-panel,
  .menu-panel__submenu,
  .search-panel__results {
    grid-template-columns: 1fr;
  }

  .menu-panel {
    display: grid;
    gap: 28px;
    padding-top: 24px;
    padding-bottom: 40px;
    background: #f5f0e8;
  }

  .menu-panel__primary,
  .menu-panel__secondary {
    display: block;
  }

  .menu-panel__item {
    padding: 15px 0;
    font-size: 25px;
    line-height: 1.15;
  }

  .menu-panel__subitem {
    min-height: 56px;
    padding: 14px;
  }

  .menu-panel__secondary p {
    margin-bottom: 16px;
  }

  .menu-panel__note {
    font-size: 20px;
  }

  .search-panel__field input {
    font-size: 32px;
  }

  .search-panel {
    padding-top: 44px;
    padding-bottom: 40px;
  }
}

@media (max-width: 480px) {
  .site-header {
    padding-left: 14px;
  }

  .site-logo {
    letter-spacing: 0.05em;
  }

  .site-logo img {
    max-width: 128px;
  }

  .icon-button {
    width: 48px;
  }

  .language-select select {
    max-width: 82px;
  }

  .menu-button,
  .close-button {
    width: 54px;
  }

  .overlay__bar {
    padding-left: 14px;
  }

  .menu-panel,
  .search-panel {
    width: calc(100vw - 28px);
  }

  .menu-panel__item {
    font-size: 23px;
  }

  .search-panel__field input {
    font-size: 28px;
  }
}
</style>
